<template>
  <b-overlay :show="loading" opacity="0.5">
    <div>
      <list-table
        :records="sample_csv_files"
        :columns="tableColumns"
        :total-records="totalRecords"
        :current-page-number.sync="currentPage"
        :search-filter.sync="searchFilter"
        :show-export-button="false"
        @row-clicked="onItemClicked"
      >
        <template #action_button>
          <b-button variant="primary" :to="{ name: 'admin-new-sample-csv-file' }">
            <span class="text-nowrap">New Sample CSV File</span>
          </b-button>
        </template>

        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                class="filter-select"
                placeholder="Status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="statusFilter"
                :options="statusOptions"
                :reduce="(val) => val.value"
                :disabled="loading"
                @input="(val) => (statusFilter = val)"
              />
            </b-col>
          </b-row>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              :to="{
                name: 'admin-update-sample-csv-file',
                params: { id: data.item._id },
              }"
            >
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Update</span>
            </b-dropdown-item>
            <b-dropdown-item
              @click="deleteSampleCSVFile(data.item._id)"
            >
              <feather-icon icon="TrashIcon" class="text-danger" />
              <span class="align-middle ml-50 text-danger">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BOverlay,
  BInputGroup,
  BInputGroupAppend,
} from "bootstrap-vue";

import { debounce, get } from "lodash";
import { formatDate } from "@/@core/utils/utils";

import vSelect from "vue-select";
import useJwt from "@/auth/jwt/useJwt";
import Ripple from "vue-ripple-directive";
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const watchHandler = {
  handler() {
    this.debounceFetchAllData();
  },
  immediate: false,
};

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BInputGroupAppend,

    vSelect,
    BOverlay,
    ListTable,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      tableColumns: [
        { key: "title", sortable: false },
        {
          key: "description",
          sortable: false
        },
        {
          key: "reference",
          sortable: false,
          label: "Reference",
        },
        { key: "status", sortable: false },
        {
          key: "created",
          sortable: false,
          formatter: (val) => `${formatDate(val)}`,
          label: "Created",
        },
        { key: "actions" },
      ],
      statusOptions: [
        { label: "Active", value: "active" },
        { label: "Deleted", value: "deleted" },
      ],
      statusFilter: "",
      searchFilter: "",
      loading: false,
      sample_csv_files: [],
      debounceFetchAllData: () => {},
    };
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler() {
        this.currentPage = 1;
        this.debounceFetchAllData();
      },
      immediate: false,
    },
    currentPage: watchHandler,
    statusFilter: watchHandler,
    searchFilter: watchHandler,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchSampleCSVFiles, 500);
    
    const { 
      status, 
      page = 1, 
      search = "", 
      limit = this.perPage 
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = search;
    this.statusFilter = status;
    this.perPage = +limit;
  },
  methods: {
    onItemClicked(rowItem) {
      this.$router.push({
        name: "admin-update-sample-csv-file",
        params: { id: rowItem._id },
      });
    },
    async fetchSampleCSVFiles() {
      try {
        this.loading = true;

        const {
          perPage: limit,
          currentPage: page,
          statusFilter: status,
          searchFilter: search,
        } = this;

        const query = {
          limit,
          page,
          status,
          search,
        };
        this.$router.push({ query }).catch(() => {});

        const request = await useJwt.mainService.sampleCSVFiles.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.sample_csv_files = data;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
        return [];
      } finally {
        this.loading = false;
      }
    },
    async deleteSampleCSVFile(id) {
      try {
        this.loading = true;
        const deleteConfirmation = await this.$swal({
          title: 'Delete this sample csv file?',
          text: "You are about to delete a sample file. Continue?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete it',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        if (!deleteConfirmation.value){
          return;
        }
        await useJwt.mainService.sampleCSVFiles.delete(id);

        const removedFieldIndex = this.sample_csv_files.map(csv_file => csv_file._id).indexOf(id);
        this.sample_csv_files.splice(removedFieldIndex, 1);
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
        return [];
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

